import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { LanguageService } from './language.service';
import { TranslationParams } from '../i18n/translations';
import { environment } from '../../../environments/environment';

type SeoConfig = {
  titleKey?: string;
  titleParams?: TranslationParams;
  descriptionKey?: string;
  descriptionParams?: TranslationParams;
  robots?: string;
  image?: string;
  ogType?: string;
  twitterCard?: string;
};

type SeoOverrides = SeoConfig & {
  title?: string;
  description?: string;
};

const DEFAULT_IMAGE = 'https://flourish.wine/assets/app-icon.png';
const DEFAULT_OG_TYPE = 'website';
const DEFAULT_ROBOTS = 'index, follow';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private initialized = false;
  private routeSeo: SeoConfig = {};
  private overrides?: SeoOverrides;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly language: LanguageService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  init(): void {
    if (this.initialized || !this.document) {
      return;
    }

    this.initialized = true;

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.routeSeo = this.extractRouteSeo();
      this.overrides = undefined;
      this.apply();
    });

    this.language.languageChanges.subscribe(() => this.apply());

    this.routeSeo = this.extractRouteSeo();
    this.apply();
  }

  setOverrides(overrides?: SeoOverrides): void {
    this.overrides = overrides;
    this.apply();
  }

  clearOverrides(): void {
    this.overrides = undefined;
    this.apply();
  }

  private extractRouteSeo(): SeoConfig {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    return (route.snapshot?.data?.['seo'] as SeoConfig) ?? {};
  }

  private apply(): void {
    if (!this.document) {
      return;
    }

    const base = this.routeSeo;
    const override = this.overrides;

    const pageTitle =
      override?.title ??
      this.resolveText(override?.titleKey, override?.titleParams) ??
      this.resolveText(base.titleKey, base.titleParams) ??
      this.resolveText('seo.home.title');

    const description =
      override?.description ??
      this.resolveText(override?.descriptionKey, override?.descriptionParams) ??
      this.resolveText(base.descriptionKey, base.descriptionParams) ??
      this.resolveText('seo.home.description');

    const finalTitle = this.formatTitle(pageTitle);
    const finalDescription = this.normalizeDescription(description);
    const image = override?.image ?? base.image ?? DEFAULT_IMAGE;
    const ogType = override?.ogType ?? base.ogType ?? DEFAULT_OG_TYPE;
    const robots = override?.robots ?? base.robots ?? DEFAULT_ROBOTS;
    const canonicalUrl = this.buildCanonicalUrl();
    const twitterCard = override?.twitterCard ?? base.twitterCard ?? (image ? 'summary_large_image' : 'summary');

    this.title.setTitle(finalTitle);
    this.meta.updateTag({ name: 'description', content: finalDescription });
    this.meta.updateTag({ name: 'robots', content: robots });

    this.meta.updateTag({ property: 'og:type', content: ogType });
    this.meta.updateTag({ property: 'og:site_name', content: this.language.translate('seo.site.name') });
    this.meta.updateTag({ property: 'og:title', content: finalTitle });
    this.meta.updateTag({ property: 'og:description', content: finalDescription });
    this.meta.updateTag({ property: 'og:image', content: image });
    if (canonicalUrl) {
      this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    }

    this.meta.updateTag({ name: 'twitter:card', content: twitterCard });
    this.meta.updateTag({ name: 'twitter:title', content: finalTitle });
    this.meta.updateTag({ name: 'twitter:description', content: finalDescription });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    if (canonicalUrl) {
      this.meta.updateTag({ name: 'twitter:url', content: canonicalUrl });
    }

    if (canonicalUrl) {
      this.setCanonicalUrl(canonicalUrl);
    }
  }

  private resolveText(key?: string, params?: TranslationParams): string | undefined {
    if (!key) {
      return undefined;
    }
    return this.language.translate(key, params);
  }

  private formatTitle(pageTitle?: string): string {
    const siteName = this.language.translate('seo.site.name');
    if (!pageTitle) {
      return siteName;
    }
    const normalizedTitle = pageTitle.toLowerCase();
    const normalizedSite = siteName.toLowerCase();
    if (normalizedTitle.includes(normalizedSite)) {
      return pageTitle;
    }
    return `${pageTitle} | ${siteName}`;
  }

  private normalizeDescription(description?: string): string {
    const fallback = this.language.translate('seo.home.description');
    const value = (description ?? fallback).replace(/\s+/g, ' ').trim();
    if (value.length <= 160) {
      return value;
    }
    return `${value.slice(0, 157).trimEnd()}...`;
  }

  private buildCanonicalUrl(): string | null {
    try {
      const base = environment.siteUrl || this.document.location?.origin;
      if (!base) {
        return null;
      }
      const normalizedBase = base.endsWith('/') ? base : `${base}/`;
      const url = new URL(this.router.url, normalizedBase);
      url.hash = '';
      url.search = '';
      return url.toString();
    } catch {
      return null;
    }
  }

  private setCanonicalUrl(url: string): void {
    const head = this.document.head;
    if (!head) {
      return;
    }

    let link = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
